import type { FieldErrors, FieldPath, FieldValues } from "react-hook-form";
import type {
  FormFieldData,
  FormInputData
} from "../Controllers/FormFieldController";
import { hasNestedValue } from "./hasNestedValue";

/**
 * Finds the first tab (in the given order) that contains fields with validation errors
 *
 * @param errors - The form validation errors from react-hook-form
 * @param formFieldData - The form field configuration data with field names and their associated tabs
 * @param tabOrder - Array of tab IDs in the order they should be prioritized
 * @returns The ID of the first tab that has errors, or null if no tab has errors
 */
export function findFirstTabWithError<T extends FieldValues>(
  errors: FieldErrors<T>,
  formFieldData: FormFieldData<T>,
  tabOrder: string[]
): string | null {
  const tabsWithErrors = new Set<string>();

  Object.values(formFieldData).forEach((fieldData) => {
    const typedFieldData = fieldData as
      FormInputData<T, FieldPath<T>> | undefined;
    if (
      typedFieldData?.name &&
      typedFieldData?.group &&
      hasNestedValue(errors, typedFieldData.name)
    ) {
      tabsWithErrors.add(typedFieldData.group);
    }
  });

  return tabOrder.find((tabId) => tabsWithErrors.has(tabId)) || null;
}
